import React, { Fragment, Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Row, Col } from 'react-flexbox-grid'
import { FormControl, FormGroup, ControlLabel } from 'react-bootstrap'
import DayPicker, { DateUtils } from 'react-day-picker'
import { Portal } from 'react-portal'
import moment from 'moment'

import PopupTypes from '../../popup_types'
import {
  GlgPopup,
  GlgPopupForm,
  GlgSelect,
  GlgDatePicker,
  GlgTooltipIcon,
  GlgButton,
} from 'SharedComponents'

class CloneEventDatesPopup extends Component {
  constructor(props) {
    super(props)
    this.onClose = props.onClose.bind(this, PopupTypes.CLONE_EVENT_DATES)
    this.handleDayClick = this.handleDayClick.bind(this)
    this.handleDayOfWeekChange = this.handleDayOfWeekChange.bind(this)
    this.handleStartDateChange = this.handleStartDateChange.bind(this)
    this.handleEndDateChange = this.handleEndDateChange.bind(this)
    this.handleFillDates = this.handleFillDates.bind(this)
    this.handlePopupClose = this.handlePopupClose.bind(this)
    this.handlePopupSave = this.handlePopupSave.bind(this)
    this.handlePopupDelete = this.handlePopupDelete.bind(this)
    this.isDayDuplicate = this.isDayDuplicate.bind(this)
    this.state = {
      selectedDays: [],
      startDate: null,
      endDate: null,
      dayOfWeek: 7,
      duplicatesPopup: {
        active: false,
        day: '',
      },
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.props.show !== nextProps.show ||
           this.state !== nextState
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      this.setState({
        selectedDays: [],
      })
    }
  }

  isLeagueProduct() {
    return this.props.data && this.props.data.fromProduct === 'league'
  }

  addSelectedDays(days) {
    const selectedDays = _.unionBy(
      this.state.selectedDays,
      days,
      day => moment(day).format('MM/DD/YYYY'),
    )
    this.setState({
      selectedDays,
    })
  }

  handleFillDates() {
    const {
      startDate,
      endDate,
      dayOfWeek,
    } = this.state

    const freeSlotsCount = this.getFreeSlotsCount()
    const startMoment = moment(startDate, 'MM/DD/YYYY')
    const endMoment = moment(endDate, 'MM/DD/YYYY')
    const days = []
    
    for (let day = startMoment; day.isSameOrBefore(endMoment) && days.length < freeSlotsCount; day.add(1, 'days')) {
      if (day.isoWeekday() === dayOfWeek) {
        const alreadySelected = _.find(this.state.selectedDays, selectedDay =>
          moment(selectedDay).format('MM/DD/YYYY') === moment(day).format('MM/DD/YYYY')
        )
        if (!alreadySelected) {
          days.push(day.toDate())
        }
      }
    }
    this.addSelectedDays(days)
  }

  handleDayClick(day, { selected }) {
    let {
      selectedDays,
      duplicatesPopup,
    } = this.state
    if (!selected) {
      // the day was not selected before, add it if free slots available
      if (this.freeSlotsExist()) {
        selectedDays = [
          ...selectedDays,
          day,
        ]
      }
    } else {
      // the day is already selected, show popup
      duplicatesPopup = {
        active: true,
        day,
      }
    }
    this.setState({
      selectedDays,
      duplicatesPopup,
    })
  }

  handleDayOfWeekChange(dayOfWeek) {
    this.setState({ dayOfWeek })
  }

  handleStartDateChange(startDate) {
    this.setState({ startDate })
  }

  handleEndDateChange(endDate) {
    this.setState({ endDate })
  }

  isFillDatesDisabled() {
    return !this.state.startDate ||
           !this.state.endDate ||
           !this.freeSlotsExist()
  }

  getPopupText() {
    const popupDayCount = this.computeDayCount(this.state.duplicatesPopup.day)

    let text = ''
    if (popupDayCount === 1) {
      text += 'There is already one round on this date.'
    } else {
      text += `There are already ${ popupDayCount } rounds on this date.`
    }

    text += ' Click '
    if (this.freeSlotsExist()) {
      text += 'OK to add another one, or '
    }

    if (popupDayCount === 1) {
      text += 'Delete to remove this round.'
    } else {
      text += 'Delete to remove these rounds.'
    }

    return text
  }

  handlePopupClose() {
    this.setState({
      duplicatesPopup: {
        active: false,
      },
    })
  }

  handlePopupSave() {
    const day = this.state.duplicatesPopup.day
    this.setState({
      selectedDays: [
        ...this.state.selectedDays,
        day,
      ],
    })
  }

  handlePopupDelete() {
    const day = this.state.duplicatesPopup.day
    const selectedDays = _.clone(this.state.selectedDays)
    _.remove(selectedDays, selectedDay =>
      DateUtils.isSameDay(selectedDay, day)
    )
    this.setState({
      selectedDays,
    })
  }

  computeDayCount(day) {
    return this.state.selectedDays.filter(selectedDay =>
      DateUtils.isSameDay(selectedDay, day)
    ).length
  }

  isDayDuplicate(day) {
    return this.computeDayCount(day) > 1
  }

  getTooltipText() {
    if (this.props.data && this.props.data.rounds) {
      return _.take(this.props.data.rounds, 25)
              .map(({ name, date }) => `${ name }, (${ date })`)
              .join('\n')
    } else {
      return ''
    }
  }

  getRoundsCount() {
    const rounds = this.props.data && this.props.data.rounds || []
    return rounds.length
  }

  getFreeSlotsCount() {
    return this.getRoundsCount() - this.state.selectedDays.length
  }

  freeSlotsExist() {
    return this.getFreeSlotsCount() > 0
  }

  render() {
    const dayPickerModifiers = {
      duplicate: this.isDayDuplicate,
    }
    const customPopupButtons = [
      {
        text: 'Delete',
        class: 'button btsp_gray_button',
        onClick: this.handlePopupDelete,
      },
    ]

    return (
      <Fragment>
        <GlgPopup title="Please select the dates for the rounds"
                  show={ this.props.show }
                  saveButtonText="Clone"
                  saveButtonEnabled={ this.state.selectedDays.length > 0 }
                  closeButtonText="Cancel"
                  onClose={ this.onClose }
                  formURL={ this.props.formURL }
                  modalClass="clone-event-dates-modal">
          <GlgPopupForm>
            {this.props.data.copyOptions && (
              <Fragment>
                <FormControl type="hidden" name="league_id" value={ this.props.data.fromLeague } />
                <FormControl type="hidden" name="product" value={ this.props.data.fromProduct } />
                <FormControl type="hidden" name="name" value={ this.props.data.leagueName } />
                <FormControl type="hidden" name="category" value={ this.props.data.category } />
                <FormControl type="hidden" name="season" value={ this.props.data.season } />
                <FormControl type="hidden" name="tour" value={ this.props.data.tour } />
                <FormControl type="hidden" name="is_express_event" value={ this.props.data.isExpressEvent } />
                <FormControl type="hidden" name="payment_model" value={ this.props.data.paymentModel } />
                <FormControl type="hidden" name="copy_options" value={ this.props.data.copyOptions } />
                <FormControl type="hidden" name="mass_dates" value={ this.state.selectedDays.map(day => moment(day).format('MM/DD/YYYY')) } />
              </Fragment>
            )}
            <h4>
              You have currently selected { this.state.selectedDays.length } dates out of { this.getRoundsCount() }.
              <GlgTooltipIcon tooltip={ this.getTooltipText() } placement="bottom" />
            </h4>
            <DayPicker numberOfMonths={3}
                       selectedDays={ this.state.selectedDays }
                       onDayClick={ this.handleDayClick }
                       modifiers={ dayPickerModifiers }
                       className={ this.freeSlotsExist() ? '' : 'unselected-disabled' } />
            {this.isLeagueProduct() && (
              <Fragment>
                <hr />
                <h4>
                  Or add a recurring round
                </h4>
                <FormGroup>
                  <Row bottom="xs">
                    <Col xs={12} sm={true}>
                      <Row>
                        <Col xs={true}>
                          <ControlLabel>
                            Start date
                          </ControlLabel>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={true}>
                          <GlgDatePicker name="start_date" onChange={ this.handleStartDateChange } />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={12} sm={true}>
                      <Row>
                        <Col xs={true}>
                          <ControlLabel>
                            End date
                          </ControlLabel>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={true}>
                          <GlgDatePicker name="end_date"
                                         onChange={ this.handleEndDateChange } />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={12} sm={true}>
                      <Row>
                        <Col xs={true}>
                          <ControlLabel>
                            Day of week
                          </ControlLabel>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={true}>
                          <GlgSelect name="day_of_week"
                                     items={ DAY_OF_WEEK_OPTIONS }
                                     onChange={ this.handleDayOfWeekChange } />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={12} sm={true}>
                      <GlgButton text="Fill in the dates"
                                 onClick={ this.handleFillDates }
                                 disabled={ this.isFillDatesDisabled() }
                                 color="gray"
                                 className="btn-fill-dates" />
                    </Col>
                  </Row>
                </FormGroup>
              </Fragment>
            )}
          </GlgPopupForm>
        </GlgPopup>
        <Portal>
          <GlgPopup title="Duplicate date"
                    show={ this.state.duplicatesPopup.active }
                    saveButtonText="OK"
                    saveButtonEnabled={ this.freeSlotsExist() }
                    onClose={ this.handlePopupClose }
                    onSave={ this.handlePopupSave }
                    customButtons={ customPopupButtons }
                    modalClass="clone-event-dates-duplicates-modal">
            { this.getPopupText() }
          </GlgPopup>
        </Portal>
      </Fragment>
    )
  }
}

CloneEventDatesPopup.propTypes = {
  show: PropTypes.bool.isRequired,
  data: PropTypes.object,
  formURL: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

const DAY_OF_WEEK_OPTIONS = [
  {
    label: 'Sunday',
    value: 7,
  },
  {
    label: 'Monday',
    value: 1,
  },
  {
    label: 'Tuesday',
    value: 2,
  },
  {
    label: 'Wednesday',
    value: 3,
  },
  {
    label: 'Thursday',
    value: 4,
  },
  {
    label: 'Friday',
    value: 5,
  },
  {
    label: 'Saturday',
    value: 6,
  },
]

export default CloneEventDatesPopup
